import React from "react";
import { AlignLeft, AlignCenter, AlignRight, Minus, Plus } from "lucide-react";
import { DEFAULT_PRINT_LAYOUT } from "@/lib/escpos";

const ROWS = [
  { k: "date", label: "Data / Nr. documento" },
  { k: "items", label: "Articoli" },
  { k: "operator", label: "Cassiere / Operatore" },
];
const ALIGNS = [
  { v: "left", Icon: AlignLeft, title: "Sinistra" },
  { v: "center", Icon: AlignCenter, title: "Centro" },
  { v: "right", Icon: AlignRight, title: "Destra" },
];
const SIZES = [
  { v: "small", label: "S", title: "Piccolo" },
  { v: "normal", label: "M", title: "Normale" },
  { v: "large", label: "G", title: "Grande" },
];
const GAPS = {
  receipt: [
    { k: "after_header", label: "Dopo intestazione" },
    { k: "after_date", label: "Dopo la data" },
    { k: "after_items", label: "Dopo gli articoli" },
    { k: "after_total", label: "Dopo il totale" },
  ],
  comanda: [
    { k: "after_header", label: "Dopo intestazione" },
    { k: "after_date", label: "Dopo la data" },
    { k: "after_items", label: "Dopo gli articoli" },
  ],
};

/**
 * Editor layout di stampa per un documento (receipt = preconto/gestionale, comanda).
 * Per ogni elemento (data, articoli, operatore): allineamento sx/centro/dx + dimensione S/M/G.
 * Più righe vuote (spaziatura) tra le sezioni del documento.
 */
export default function PrintLayoutEditor({ doc, layout, onChange, testid = "playout" }) {
  const get = (k) => ({ ...DEFAULT_PRINT_LAYOUT[doc][k], ...((layout || {})[doc]?.[k] || {}) });
  const set = (k, patch) => {
    const next = { ...(layout || {}) };
    next[doc] = { ...(next[doc] || {}), [k]: { ...get(k), ...patch } };
    onChange(next);
  };
  const getSp = (k) => {
    const v = ((layout || {})[doc]?.spacing || {})[k];
    return v != null ? Number(v) : DEFAULT_PRINT_LAYOUT[doc].spacing[k];
  };
  const setSp = (k, v) => {
    const next = { ...(layout || {}) };
    next[doc] = { ...(next[doc] || {}), spacing: { ...((layout || {})[doc]?.spacing || {}), [k]: Math.max(0, Math.min(5, v)) } };
    onChange(next);
  };

  return (
    <div className="space-y-2.5">
      {(Array.isArray(ROWS) ? ROWS : []).map(({ k, label }) => {
        const cur = get(k);
        return (
          <div key={k} className="flex items-center gap-2">
            <span className="text-xs font-medium w-36 shrink-0">{label}</span>
            <div className="flex rounded-md border border-velta-border overflow-hidden">
              {(Array.isArray(ALIGNS) ? ALIGNS : []).map(({ v, Icon, title }) => (
                <button
                  key={v}
                  type="button"
                  title={title}
                  data-testid={`${testid}-${k}-align-${v}`}
                  onClick={() => set(k, { align: v })}
                  className={`w-9 h-9 flex items-center justify-center ${cur.align === v ? "bg-velta-ink text-white" : "bg-white text-velta-mute hover:text-velta-ink"}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
            <div className="flex rounded-md border border-velta-border overflow-hidden">
              {(Array.isArray(SIZES) ? SIZES : []).map(({ v, label: sl, title }) => (
                <button
                  key={v}
                  type="button"
                  title={title}
                  data-testid={`${testid}-${k}-size-${v}`}
                  onClick={() => set(k, { size: v })}
                  className={`w-8 h-9 text-xs font-mono ${cur.size === v ? "bg-velta-ink text-white" : "bg-white text-velta-mute hover:text-velta-ink"}`}
                >
                  {sl}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <div className="pt-2 border-t border-dashed border-velta-border">
        <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-2">Righe vuote (spaziatura)</div>
        <div className="space-y-1.5">
          {GAPS[doc].map(({ k, label }) => (
            <div key={k} className="flex items-center gap-2">
              <span className="text-xs w-36 shrink-0">{label}</span>
              <div className="flex items-center rounded-md border border-velta-border overflow-hidden">
                <button
                  type="button"
                  data-testid={`${testid}-sp-${k}-minus`}
                  onClick={() => setSp(k, getSp(k) - 1)}
                  className="w-9 h-8 flex items-center justify-center text-velta-mute hover:text-velta-ink hover:bg-velta-bg"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span data-testid={`${testid}-sp-${k}-value`} className="w-8 text-center text-xs font-mono font-bold">
                  {getSp(k)}
                </span>
                <button
                  type="button"
                  data-testid={`${testid}-sp-${k}-plus`}
                  onClick={() => setSp(k, getSp(k) + 1)}
                  className="w-9 h-8 flex items-center justify-center text-velta-mute hover:text-velta-ink hover:bg-velta-bg"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <span className="text-[10px] text-velta-mute">righe</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
